import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  TextField,
  IconButton,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Collapse,
  Divider,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  AddComment as NewChatIcon,
  KeyboardArrowDown as ScrollDownIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { sendAIChatMessage, endAIChatSession } from '../../services/api';

/**
 * AIChatModal - Modular AI chat interface
 *
 * This component can be used from any page. It receives context
 * that describes what page it's on, what data is available, and
 * whether the user has completed all required inputs.
 *
 * The AI behavior adapts based on context completeness:
 * - Complete context: Substantive financial analysis
 * - Partial context: Helpful form guidance
 */
function AIChatModal({ open, onClose, context }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showContext, setShowContext] = useState(false);
  const [error, setError] = useState(null);
  const [showScrollButton, setShowScrollButton] = useState(false);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Detect if user has scrolled up
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    setShowScrollButton(!isNearBottom && messages.length > 2);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Focus input when modal opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Reset state when modal opens with new context
  useEffect(() => {
    if (open) {
      // Only reset if context page changed
      setError(null);
    }
  }, [open, context?.page]);

  const handleSend = async () => {
    const message = inputValue.trim();
    if (!message || isLoading) return;

    setInputValue('');
    setError(null);

    // Add user message to UI immediately
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      // Build conversation history for context
      const conversationHistory = messages.map(m => ({
        role: m.role,
        content: m.content,
      }));

      // Send to API
      const response = await sendAIChatMessage({
        message,
        session_id: sessionId,
        context: buildContextPayload(context),
        conversation_history: conversationHistory,
      });

      // Update session ID if new
      if (response.data.session_id) {
        setSessionId(response.data.session_id);
      }

      // Add assistant response
      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString(),
        tokens: response.data.tokens_used,
      };
      setMessages(prev => [...prev, assistantMessage]);

    } catch (err) {
      console.error('Chat error:', err);
      setError(err.response?.data?.error || 'Failed to send message. Please try again.');

      // Add error message to UI
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: 'I apologize, but I encountered an error processing your request. Please try again.',
        timestamp: new Date().toISOString(),
        isError: true,
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = async () => {
    // End current session if exists
    if (sessionId) {
      try {
        await endAIChatSession(sessionId);
      } catch (err) {
        console.warn('Failed to end session:', err);
        // Continue anyway - not critical
      }
    }

    // Clear local state
    setMessages([]);
    setSessionId(null);
    setError(null);
    setInputValue('');
  };

  const handleCopyChat = async () => {
    if (messages.length === 0) return;

    const chatText = messages
      .map((msg) => `${msg.role === 'user' ? 'You' : 'AI'}: ${msg.content}`)
      .join('\n\n');

    try {
      await navigator.clipboard.writeText(chatText);
      // Could add a snackbar notification here
    } catch (err) {
      console.error('Failed to copy chat:', err);
    }
  };

  const buildContextPayload = (ctx) => {
    if (!ctx) {
      return {
        page: 'general',
        purpose: 'General financial assistance',
        is_complete: false,
        current_values: {},
      };
    }

    return {
      page: ctx.page || 'unknown',
      purpose: ctx.purpose || 'General assistance',
      workflow: ctx.workflow || null,
      schema_fields: ctx.schema_fields || ctx.schemaFields || null,
      current_values: ctx.current_values || ctx.currentValues || {},
      computed_results: ctx.computed_results || ctx.computedResults || null,
      is_complete: ctx.is_complete ?? ctx.isComplete ?? false,
      missing_required: ctx.missing_required || ctx.missingRequired || [],
      hints: ctx.hints || null,
    };
  };

  const isComplete = context?.is_complete ?? context?.isComplete ?? false;

  const getContextStatusChip = () => {
    if (!context) {
      return <Chip label="General Help" size="small" color="default" />;
    }
    if (isComplete) {
      return <Chip label="Full Context" size="small" color="success" />;
    }
    return <Chip label="Partial Context" size="small" color="warning" />;
  };

  const getWelcomeMessage = () => {
    if (!context) {
      return "Hi! I'm your AlloFicient assistant. How can I help you today?";
    }
    if (isComplete) {
      return `I have full context about your ${context.page?.replace(/_/g, ' ')}. Ask me anything about your scenario!`;
    }
    return `I can help you with the ${context.page?.replace(/_/g, ' ')}. Complete the form for detailed analysis, or ask me questions about how to fill it out.`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
          maxHeight: 600,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          flexDirection: 'column',
          pb: 1,
        }}
      >
        {/* Top row: Title and action buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BotIcon color="primary" />
            <Typography variant="h6">AI Assistant</Typography>
            {/* Desktop: Show chip inline */}
            {!isMobile && getContextStatusChip()}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {messages.length > 0 && (
              <>
                <Tooltip title="Copy chat">
                  <IconButton onClick={handleCopyChat} size="small">
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Start new chat">
                  <IconButton onClick={handleNewChat} size="small" disabled={isLoading}>
                    <NewChatIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        {/* Mobile: Show chip on its own row, centered */}
        {isMobile && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            {getContextStatusChip()}
          </Box>
        )}
      </DialogTitle>

      <Divider />

      {/* Context Info (Collapsible) */}
      {context && (
        <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={() => setShowContext(!showContext)}
          >
            <InfoIcon fontSize="small" sx={{ mr: 1 }} color="action" />
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              {context.purpose || 'Current page context'}
            </Typography>
            {showContext ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>
          <Collapse in={showContext}>
            <Box sx={{ mt: 1, pl: 4 }}>
              <Typography variant="caption" color="text.secondary">
                Page: {context.page}<br />
                Status: {context.isComplete ? 'Complete' : 'Incomplete'}
                {context.missingRequired?.length > 0 && (
                  <><br />Missing: {context.missingRequired.join(', ')}</>
                )}
              </Typography>
            </Box>
          </Collapse>
        </Box>
      )}

      {/* Messages Area */}
      <DialogContent
        ref={messagesContainerRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          py: 2,
          position: 'relative',
        }}
      >
        {/* Welcome message if no messages yet */}
        {messages.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <BotIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              {getWelcomeMessage()}
            </Typography>
          </Box>
        )}

        {/* Message list */}
        {messages.map((msg) => (
          <Box
            key={msg.id}
            sx={{
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            }}
          >
            <Paper
              elevation={1}
              sx={{
                p: 1.5,
                maxWidth: '80%',
                bgcolor: msg.role === 'user' ? 'primary.main' : msg.isError ? 'error.light' : 'grey.100',
                color: msg.role === 'user' ? 'white' : 'text.primary',
                borderRadius: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                {msg.role === 'assistant' && (
                  <BotIcon fontSize="small" sx={{ mt: 0.5 }} />
                )}
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {msg.content}
                  </Typography>
                  {msg.tokens && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 0.5,
                        opacity: 0.7,
                      }}
                    >
                      {msg.tokens.prompt + msg.tokens.completion} tokens
                    </Typography>
                  )}
                </Box>
                {msg.role === 'user' && (
                  <PersonIcon fontSize="small" sx={{ mt: 0.5 }} />
                )}
              </Box>
            </Paper>
          </Box>
        ))}

        {/* Loading indicator */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Paper
              elevation={1}
              sx={{
                p: 1.5,
                bgcolor: 'grey.100',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Thinking...
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Error message */}
        {error && (
          <Typography variant="caption" color="error" sx={{ textAlign: 'center' }}>
            {error}
          </Typography>
        )}

        <div ref={messagesEndRef} />
      </DialogContent>

      {/* Scroll to bottom button */}
      {showScrollButton && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1,
          }}
        >
          <Tooltip title="Scroll to latest">
            <IconButton
              size="small"
              onClick={scrollToBottom}
              sx={{
                bgcolor: 'background.paper',
                boxShadow: 2,
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              <ScrollDownIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <Divider />

      {/* Input Area */}
      <Box sx={{ p: 2, display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder={isComplete
            ? "Ask about your scenario..."
            : "Ask a question or get help..."
          }
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          inputRef={inputRef}
          disabled={isLoading}
          multiline
          maxRows={3}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={!inputValue.trim() || isLoading}
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            '&:hover': { bgcolor: 'primary.dark' },
            '&:disabled': { bgcolor: 'grey.300' },
          }}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Dialog>
  );
}

export default AIChatModal;
